import { useTranslator } from '@Localization';
import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { Children, cloneElement, isValidElement, ReactNode } from 'react';
import { IPermissionCheckResult } from '../../modules/permissions/permissionsHelpers';
import { ICurrentWorkspaceStore } from '../../modules/workspaces/currentWorkspace/CurrentWorkspace.store';

interface IProps {
  hasPermission: IPermissionCheckResult;
  hideWhenNoAccess?: boolean;
  disableForEncryption?: boolean;
  children: ReactNode;
}

export const EnableFor = observer((props: IProps): ReactNode => {
  const currentWorkspaceStore = useInjection(ICurrentWorkspaceStore.$);
  const { t } = useTranslator();

  const { currentWorkspace } = currentWorkspaceStore;

  const hasEncryptionAccess = props.disableForEncryption ? !currentWorkspaceStore.currentWorkspace.encryption.encrypted : true;
  const isLocked = currentWorkspace.locked;

  const hasAccess = props.hasPermission.allowed && hasEncryptionAccess && !isLocked;

  const getTitle = (childTitle: string | undefined): string | undefined => {
    if (hasAccess) {
      return childTitle;
    }

    if (!hasEncryptionAccess) {
      return t('not_available_for_encrypted_workspace');
    }

    return props.hasPermission.reason ?? t('you_do_not_have_permission_to_perform_this_operation');
  };

  if (props.hideWhenNoAccess && !hasAccess) {
    return <></>;
  }

  return Children.map(props.children, (child): ReactNode => {
    if (isValidElement<{ disabled: boolean; title?: string }>(child)) {
      return cloneElement(child, {
        disabled: child.props.disabled || !hasAccess,
        title: getTitle(child.props.title),
      });
    }
    return child;
  });
});
